/* import __COLOCATED_TEMPLATE__ from './help-center-audience-dropdown.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { helper } from '@ember/component/helper';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import {
  type HelpCenterAudienceBulkActionApiParams,
  type HelpCenterAudienceRole,
  USER_ROLE,
  VISITOR_ROLE,
  LEAD_ROLE,
} from 'embercom/components/knowledge-hub/filterable-list/bulk-actions/bulk-action-helper';

export const HELP_CENTER_AUDIENCE_ROLES = [
  {
    value: USER_ROLE,
    translationKey:
      'knowledge-hub.filterable-list.bulk-actions.help-center-audience-dropdown.users' as const,
  },
  {
    value: VISITOR_ROLE,
    translationKey:
      'knowledge-hub.filterable-list.bulk-actions.help-center-audience-dropdown.visitors' as const,
  },
  {
    value: LEAD_ROLE,
    translationKey:
      'knowledge-hub.filterable-list.bulk-actions.help-center-audience-dropdown.leads' as const,
  },
] as const;

interface Signature {
  Args: {
    item: {
      selectedFragments: Set<ContentFragment>;
      applyHelpCenterAudience: (params: HelpCenterAudienceBulkActionApiParams) => Promise<void>;
    };
  };
}

export default class HelpCenterAudienceDropdown extends Component<Signature> {
  readonly roles = HELP_CENTER_AUDIENCE_ROLES;
  @tracked activeRoles: HelpCenterAudienceRole[] = [];

  private get selectedFragmentsList(): ContentFragment[] {
    return Array.from(this.args.item.selectedFragments).filter((fragment) => fragment != null);
  }

  shouldShowRoleTick = helper(function ([role, selectedFragments]: [
    string,
    Set<ContentFragment>,
  ]): boolean {
    return Array.from(selectedFragments)
      .filter((fragment) => fragment != null)
      .every((fragment) => fragment.helpCenterAudienceRoles?.includes(role));
  });

  isRoleActive = helper(function ([role, activeRoles]: [string, string[]]): boolean {
    return activeRoles.includes(role);
  });

  @action
  async toggleRole(role: HelpCenterAudienceRole) {
    if (this.activeRoles.includes(role)) {
      return;
    }
    this.activeRoles = [...this.activeRoles, role];

    let isAppliedToAll = this.selectedFragmentsList.every((fragment) =>
      fragment.helpCenterAudienceRoles?.includes(role),
    );

    if (isAppliedToAll) {
      await this.args.item.applyHelpCenterAudience({ roles_to_remove: [role] });
    } else {
      await this.args.item.applyHelpCenterAudience({ roles_to_add: [role] });
    }

    this.activeRoles = this.activeRoles.filter((r) => r !== role);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::BulkActions::HelpCenterAudienceDropdown': typeof HelpCenterAudienceDropdown;
    'knowledge-hub/filterable-list/bulk-actions/help-center-audience-dropdown': typeof HelpCenterAudienceDropdown;
  }
}
